import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { PlannerErrorPage } from "@/components/planner-error-page";
import { PlannerDrawingLoadingOverlay } from "@/components/planner-loading";
import { PlannerViewer } from "@/components/planner-viewer";
import { resolveTakeoffDrawingFile } from "@/lib/takeoff-drawing-file";
import { orpc } from "@/utils/orpc";

const idSchema = z.uuid();

export const Route = createFileRoute(
	"/project/$projectId/takeoff/$takeoffId/drawing/$fileId",
)({
	component: TakeoffDrawingRoute,
	// `?focusEntry=<id>` deep-links from the Määräluettelo "show in viewer" link;
	// the viewer selects and centers that Entry once its data is ready.
	validateSearch: (
		search: Record<string, unknown>,
	): { focusEntry?: string } => {
		const focus = search.focusEntry;
		return typeof focus === "string" && idSchema.safeParse(focus).success
			? { focusEntry: focus }
			: {};
	},
});

function TakeoffDrawingRoute(): ReactElement {
	const { t } = useTranslation();
	const { projectId, takeoffId, fileId } = Route.useParams();
	const { focusEntry } = Route.useSearch();
	const paramsValid =
		idSchema.safeParse(projectId).success &&
		idSchema.safeParse(takeoffId).success &&
		idSchema.safeParse(fileId).success;
	const takeoffQuery = useQuery({
		...orpc.takeoffs.get.queryOptions({ input: { takeoffId } }),
		enabled: paramsValid,
	});

	if (!paramsValid) {
		return (
			<PlannerErrorPage
				description={t("errors.invalidTakeoffLinkDescription")}
				title={t("errors.invalidTakeoffLink")}
			/>
		);
	}
	if (takeoffQuery.isLoading) {
		// Same stepper the viewer shows while it loads drawing data, so a page
		// reload renders one continuous loading UI instead of a spinner flash.
		return (
			<main className="relative flex h-screen flex-col overflow-hidden bg-background text-foreground">
				<PlannerDrawingLoadingOverlay stage="initializing" />
			</main>
		);
	}
	if (takeoffQuery.isError || !takeoffQuery.data) {
		return (
			<PlannerErrorPage
				description={t("errors.invalidTakeoffLinkDescription")}
				title={t("errors.invalidTakeoffLink")}
			/>
		);
	}

	const resolution = resolveTakeoffDrawingFile(takeoffQuery.data.files, fileId);
	if ("error" in resolution) {
		return (
			<PlannerErrorPage
				description={t("errors.invalidDrawingLinkDescription")}
				title={t("errors.invalidDrawingLink")}
			/>
		);
	}

	return (
		<PlannerViewer
			discipline={takeoffQuery.data.discipline}
			drawingId={resolution.drawingId}
			fileId={fileId}
			focusEntryId={focusEntry ?? null}
			takeoffId={takeoffId}
		/>
	);
}
